import { isMac } from "../helpers/main";
import { ISpreadsheetConfig, ToolbarBlocks } from "../types";
import { getColorpickerTemplate, getFormatsDropdown } from "./../helpers/toolbar";
import locale from "./../locales/en";

const ctrlhotKeysWin = {
	undo: "z",
	redo: "y",
	bold: "b",
	italic: "i",
	underline: "u",
	link: "k",
	"align-left": "l",
	"align-center": "e",
	"align-right": "r",
};
const altKeysWin = {
	"line-through": "shift+5"
};
const hotKeysMac = {
	...ctrlhotKeysWin,
	...altKeysWin,
	"line-through": "shift+x"
};

export function getHotKey(name: string): string {
	// eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
	if (isMac()) {
		return `\u2318+${hotKeysMac[name]}`;
	}
	return ctrlhotKeysWin[name] ? `ctrl+${ctrlhotKeysWin[name]}` : `alt+${altKeysWin[name]}`;
}

export function getToolbarStruct(elements: ToolbarBlocks[], config: ISpreadsheetConfig): any[] {
	const toolbarBlocks = {
		undo: [
			{
				id: "undo",
				type: "navItem",
				icon: "dxi dxi-undo",
				tooltip: `${locale.undo} (${getHotKey("undo")})`,
			},
			{
				id: "redo",
				type: "navItem",
				icon: "dxi dxi-redo",
				tooltip: `${locale.redo} (${getHotKey("redo")})`,
			},
		],
		colors: [
			{
				id: "color",
				css: "dhx_button-toolbar-colorPicker",
				type: "HTMLButton",
				html: getColorpickerTemplate("#4C4C4C", "format-color-text"),
				tooltip: locale.textColor,
			},
			{
				id: "background",
				css: "dhx_button-toolbar-colorPicker",
				type: "HTMLButton",
				html: getColorpickerTemplate("#FFF", "format-color-fill"),
				tooltip: locale.backgroundColor,
			},
		],
		lock: [
			{
				id: "lock",
				type: "navItem",
				icon: "dxi dxi-key",
				tooltip: locale.lockCell,
			},
		],
		file: [
			{
				id: "export",
				type: "navItem",
				icon: "dxi dxi-file-export",
				tooltip: locale.export,
				items: [
					{
						id: "export-xlsx",
						value: "Microsoft Excel(.xlsx)",
						icon: "dxi dxi-file-excel",
					},
				],
			},
			{
				id: "import",
				type: "navItem",
				icon: "dxi dxi-file-import",
				tooltip: locale.import,
				items: [
					{
						id: "import-xlsx",
						value: "Microsoft Excel(.xlsx)",
						icon: "dxi dxi-file-excel",
					},
				],
			},
		],
		columns: [
			{
				id: "add-col",
				type: "navItem",
				icon: "dxi dxi-table-column-plus-before",
				tooltip: locale.addColumn,
			},
			{
				id: "remove-col",
				type: "navItem",
				icon: "dxi dxi-table-column-remove",
				tooltip: locale.removeColumn,
			},
		],
		rows: [
			{
				id: "add-row",
				type: "navItem",
				icon: "dxi dxi-table-row-plus-before",
				tooltip: locale.addRow,
			},
			{
				id: "remove-row",
				type: "navItem",
				icon: "dxi dxi-table-row-remove",
				tooltip: locale.removeRow,
			},
		],
		clear: [
			{
				type: "navItem",
				icon: "dxi dxi-eraser",
				id: "clear-group",
				tooltip: locale.clear,
				items: [
					{
						id: "clear-value",
						value: locale.clearValue,
					},
					{
						id: "clear-styles",
						value: locale.clearStyles,
					},
					{
						id: "clear-all",
						value: locale.clearAll,
					},
				],
			},
		],
		align: [
			{
				id: "halign",
				type: "navItem",
				icon: "dxi dxi-format-align-left",
				tooltip: locale.halign,
				items: [
					{
						id: "halign-left",
						type: "navItem",
						value: locale.left,
						icon: "dxi dxi-format-align-left",
						group: "halign",
						tooltip: `${locale.left} (${getHotKey("align-left")})`,
					},
					{
						id: "halign-center",
						type: "navItem",
						value: locale.center,
						icon: "dxi dxi-format-align-center",
						group: "halign",
						tooltip: `${locale.center} (${getHotKey("align-center")})`,
					},
					{
						id: "halign-right",
						type: "navItem",
						value: locale.right,
						icon: "dxi dxi-format-align-right",
						group: "halign",
						tooltip: `${locale.right} (${getHotKey("align-right")})`,
					},
				],
			},
			{
				id: "valign",
				type: "navItem",
				icon: "dxi dxi-format-align-left",
				tooltip: locale.valign,
				items: [
					{
						id: "valign-top",
						type: "navItem",
						group: "valign",
						value: locale.top,
						icon: "dxi dxi-format-vertical-align-top",
					},
					{
						id: "valign-center",
						type: "navItem",
						group: "valign",
						value: locale.center,
						icon: "dxi dxi-format-vertical-align-center",
					},
					{
						id: "valign-bottom",
						type: "navItem",
						group: "valign",
						value: locale.bottom,
						icon: "dxi dxi-format-vertical-align-bottom",
					},
				],
			},
			{
				id: "multiline",
				type: "navItem",
				icon: "dxi dxi-format-align-left",
				tooltip: locale.multiline,
				items: [
					{
						id: "multiline-clip",
						type: "navItem",
						group: "multiline",
						value: locale.clip,
						icon: "dxi dxi-format-text-wrapping-clip",
					},
					{
						id: "multiline-wrap",
						type: "navItem",
						group: "multiline",
						value: locale.wrap,
						icon: "dxi dxi-format-text-wrapping-wrap",
					},
				],
			},
			{
				id: "merge",
				type: "navItem",
				icon: "dxi dxi-table-merge-cells",
				tooltip: locale.merge,
			},
		],
		decoration: [
			{
				id: "font-weight-bold",
				type: "navItem",
				icon: "dxi dxi-format-bold",
				tooltip: `${locale.bold} (${getHotKey("bold")})`,
			},
			{
				id: "font-style-italic",
				type: "navItem",
				icon: "dxi dxi-format-italic",
				tooltip: `${locale.italic} (${getHotKey("italic")})`,
			},
			{
				id: "text-decoration-underline",
				type: "navItem",
				icon: "dxi dxi-format-underline",
				tooltip: `${locale.underline} (${getHotKey("underline")})`,
			},
			{
				id: "text-decoration-line-through",
				type: "navItem",
				icon: "dxi dxi-format-strikethrough",
				tooltip: `${locale.strikethrough} (${getHotKey("line-through")})`,
			},
		],
		helpers: [
			{
				id: "link",
				type: "navItem",
				icon: "dxi dxi-link-variant",
				tooltip: `${locale.insert} ${locale.link.toLowerCase()} (${getHotKey("link")})`,
			},
			{
				id: "help",
				type: "navItem",
				icon: "dxi dxi-help-circle-outline",
				tooltip: locale.help,
			},
		],
		format: [
			{
				id: "format",
				type: "navItem",
				tooltip: locale.format,
				css: "dhx_format-dropdown",
				items: getFormatsDropdown(config),
			},
		],
		actions: [
			{
				id: "filter",
				type: "navItem",
				tooltip: locale.filter,
				icon: "dxi dxi-filter-variant",
			},
		]
	};
	const defaultBlocks = ["undo", "colors", "decoration", "align", "format", "actions", "helpers"] as ToolbarBlocks[];
	elements = elements || defaultBlocks;

	const defaultBlockIndex = elements.indexOf("default");

	if (defaultBlockIndex !== -1) {
		elements.splice(defaultBlockIndex, 1, ...defaultBlocks);
	}

	elements = elements.filter((item, i, arr) => arr.indexOf(item) === i) as ToolbarBlocks[];

	return elements.reduce((struct, item, i, arr) => {
		if (toolbarBlocks[item]) {
			struct.push(...toolbarBlocks[item]);
			if (i !== arr.length - 1) {
				struct.push({
					type: "separator",
				} as any);
			}
		}
		return struct;
	}, [] as any);
}
